import { cache } from 'react';
import { resolveToggleDefault } from '@/lib/helpers';

// Request-scoped store for values that deeply-nested server components need
// without prop-drilling through the Storyblok render tree. React's cache()
// returns one object per request: the locale page populates it (see
// app/[locale]/[[...slug]]/page.js) and descendants like Box read it.
//
// This deliberately avoids next/headers — the component registry (storyblok.js)
// is imported by the client-side StoryblokProvider, so anything Box imports must
// be safe in the client bundle. `cache` is; next/headers is not.
export const getRequestContext = cache(() => ({ language: 'en', toggleDefaults: {}, bannerDismissedId: '' }));

/**
 * Record every Toggle's starting option, keyed by its Group ID, so that each
 * ToggleTarget renders the right thing on the server.
 *
 * A target sits elsewhere in the blok tree and can't see its control, so on its
 * own it has to guess — and a toggle that starts on "none" would show its
 * primary target for a frame before hydration hid it again. Walking the story
 * once up front removes the guess. Groups with no toggle simply aren't listed,
 * and the target falls back to 'primary' as before.
 */
export function collectToggleDefaults(content) {
  const defaults = {};
  const walk = (node) => {
    if (Array.isArray(node)) return node.forEach(walk);
    if (!node || typeof node !== 'object') return;
    if (node.component === 'toggle' && node.group) defaults[node.group] = resolveToggleDefault(node);
    // A Shared Block can start its toggles switched on (see SharedBlock).
    if (node.component === 'shared_block' && node.toggles_on) {
      walkSwitchedOn(node.story);
      return;
    }
    for (const value of Object.values(node)) if (value && typeof value === 'object') walk(value);
  };
  const walkSwitchedOn = (node) => {
    if (Array.isArray(node)) return node.forEach(walkSwitchedOn);
    if (!node || typeof node !== 'object') return;
    if (node.component === 'toggle' && node.group) defaults[node.group] = 'secondary';
    for (const value of Object.values(node)) if (value && typeof value === 'object') walkSwitchedOn(value);
  };
  walk(content);
  getRequestContext().toggleDefaults = defaults;
}
